import {computed, makeObservable, observable, runInAction} from "mobx";
import i18n from "../../../../i18n";
import {BooleanObservable} from "../../../observables/booleanObservable";
import {ObservableList} from "../../../observables/observableList";
import {RadioGroupKeyValue} from "../../../observables/radioGroupKeyValues/radioGroupKeyValue";
import {SimpleRadioGroupKeyValue} from "../../../observables/radioGroupKeyValues/simpleRadioGroupKeyValue";
import {TextRadioGroupKeyValue} from "../../../observables/radioGroupKeyValues/textRadioGroupKeyValue";
import {TextObservable} from "../../../observables/textObservable";
import ResidentAccountCreateDetails from "./residentAccountCreateDetails";
import ResidentDetail from "./residentDetail";
import {AppAccessStatus} from "../../../../shared/enums/appAccessStatus";

export default class ResidentModalDetail {
  private emailInputKey = "emailInput";

  radioGroupItems: ObservableList<RadioGroupKeyValue>;
  emailTextObservable: TextObservable;
  isEditingActiveResident: BooleanObservable;

  appAccessStatus?: AppAccessStatus;
  residentId: string;
  firstName: string;
  lastName: string;
  preferredName: string;
  appAccountEmail?: string;
  phoneNumber?: string;
  mobileNumber?: string;
  unitCode: string;
  townhouseBuddy?: ResidentDetail;
  hasActiveAccount: boolean;
  tempPassword?: string;

  // Persist password throughout api requests
  password?: string;

  constructor(resident: ResidentDetail, clearErrorMessage?: () => void) {
    makeObservable(this, {
      appAccountEmail: observable,
      hasActiveAccount: observable,
      createAccountDisabled: computed,
      radioGroupHasError: computed,
      editEmailHasError: computed
    });

    this.appAccessStatus = resident.appAccessStatus;
    this.residentId = resident.platformUserId;
    this.firstName = resident.firstName;
    this.lastName = resident.lastName;
    this.preferredName = resident.preferredName;
    this.appAccountEmail = resident.appAccountEmail;
    this.phoneNumber = resident.phoneNumber;
    this.mobileNumber = resident.mobileNumber;
    this.unitCode = resident.unitCode;
    this.townhouseBuddy = resident.townhouseBuddy;
    this.hasActiveAccount = resident.hasActiveAccount;
    this.appAccessStatus = resident.appAccessStatus;

    this.emailTextObservable = new TextObservable(
      resident.hasActiveAccount ? resident.appAccountEmail! : "",
      undefined,
      (toValidate) => {
        return toValidate.trim().length > 0;
      },
      clearErrorMessage
    );
    this.isEditingActiveResident = new BooleanObservable(false);

    this.radioGroupItems = this.formatRadioGroup(resident);
  }

  private formatRadioGroup = (
    resident: ResidentDetail | null | undefined
  ): ObservableList<RadioGroupKeyValue> => {
    const listItems: RadioGroupKeyValue[] = [];

    // Add existing email if one exists
    if (resident?.emailAddress) {
      listItems.push(
        new SimpleRadioGroupKeyValue(
          resident.emailAddress,
          resident.emailAddress
        )
      );
    }

    // Always add free input
    listItems.push(
      new TextRadioGroupKeyValue(
        this.emailInputKey,
        i18n.t("residentadmin:labels.addEmail"),
        this.emailTextObservable,
        i18n.t("residentadmin:labels.addEmailPlaceholder")
      )
    );

    return new ObservableList(
      listItems,
      resident?.emailAddress ?? this.emailInputKey
    );
  };

  resetEditEmail = () => {
    this.emailTextObservable.setText(this.appAccountEmail!);
    this.isEditingActiveResident.setBoolean(false);
  };

  setAppAccountEmail(appAccountEmail: string): void {
    runInAction(() => {
      this.appAccountEmail = appAccountEmail;
      this.hasActiveAccount = !!appAccountEmail;
    });
    this.emailTextObservable.setText(appAccountEmail);
    this.isEditingActiveResident.setBoolean(false);
  }

  setPassword(password: string): void {
    this.password = password;
  }

  setTempPassword(tempPassword?: string): void {
    this.tempPassword = tempPassword;
  }

  get createAccountDisabled(): boolean {
    return (
      this.radioGroupItems.selectedItem?.value === this.emailInputKey &&
      this.emailTextObservable.text.trim().length === 0
    );
  }

  get editEmailDisabled(): boolean {
    return this.emailTextObservable.text.trim().length === 0;
  }

  get enteredEmail(): string {
    if (!this.hasActiveAccount) {
      // Use radio group
      return this.radioGroupItems.selectedItem!.enteredValue.trim();
    }
    // Do not use radio group, just text observable
    return this.emailTextObservable.text.trim();
  }

  get formattedResidentName(): string {
    const {firstName, lastName, preferredName} = this;
    if (firstName && lastName && preferredName && firstName !== preferredName) {
      return `${lastName}, ${firstName} (${preferredName})`;
    }
    if (firstName && lastName) {
      return `${lastName}, ${firstName}`;
    }
    if (preferredName) {
      return preferredName;
    }

    return "";
  }

  get residentAccountCreateDetails(): ResidentAccountCreateDetails {
    return new ResidentAccountCreateDetails(
      this.residentId,
      this.firstName,
      this.lastName,
      this.enteredEmail
    );
  }

  get radioGroupHasError(): boolean {
    return (
      this.radioGroupItems.selectedItem?.value === this.emailInputKey &&
      !this.emailTextObservable.isValid
    );
  }

  get editEmailHasError(): boolean {
    return !this.emailTextObservable.isValid;
  }
}
